import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useUser, useAuthActions, LoginForm, RegisterForm, PasswordResetRequest, PasswordResetForm, EmailVerification, TfaVerification } from '@hiyve/react-identity';
import { useAppUser } from './hooks/useAppUser';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PaymentIcon from '@mui/icons-material/Payment';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// Feature pages
import Dashboard from './features/dashboard/pages/Dashboard';
import LiveRooms from './features/rooms/pages/LiveRooms';
import Rooms from './features/rooms/pages/Rooms';
import RoomDetails from './features/rooms/pages/RoomDetails';
import Recordings from './features/recordings/pages/Recordings';
import RecordingDetails from './features/recordings/pages/RecordingDetails';
import ApiKeyManagement from './features/organizations/pages/ApiKeyManagement';
import BillingSettings from './features/billing/pages/BillingSettings';
import UsageDashboard from './features/usage/pages/UsageDashboard';
import TeamManagement from './features/members/pages/TeamManagement';
import AccountSettings from './features/settings/pages/AccountSettings';
import OrganizationSetup from './features/organizations/pages/OrganizationSetup';
import VerificationPending from './features/auth/pages/VerificationPending';
import AdminDashboard from './features/admin/pages/AdminDashboard';
import MigrationsSupport from './features/migrations/pages/MigrationsSupport';
import UserRemoval from './features/userRemoval/pages/UserRemoval';
import UserManagement from './features/users/pages/UserManagement';
import UserDetail from './features/users/pages/UserDetail';
import AdminUsers from './features/admin/pages/AdminUsers';
import AdminUserDetails from './features/admin/pages/AdminUserDetails';
import AdminNotifications from './features/admin/pages/AdminNotifications';
import AdminInfrastructure from './features/admin/pages/AdminInfrastructure';
import AdminServerStatus from './features/admin/pages/AdminServerStatus';
import AdminFileManager from './features/admin/pages/AdminFileManager';

const drawerWidth = 240;

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  palette: {
    mode: 'dark',
    primary: { main: '#2979ff' },
    secondary: { main: '#00e5ff' },
    background: { default: '#0d1117', paper: '#161b22' },
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: '0 1px 3px 0 rgba(0,0,0,0.4)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '1px 0 3px 0 rgba(0,0,0,0.4)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          border: 'none',
          height: 1,
          backgroundImage: 'linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '&.MuiPaper-outlined': {
            boxShadow: '0 1px 6px 0 rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { boxShadow: '0 1px 3px 0 rgba(0,0,0,0.4)' },
      },
    },
  },
});

// Navigation items for the sidebar
// muzie.live tenant owner — the ONLY account that sees the Migrations page
// (support surface for muzie.studio→muzie.live migrations). Server enforces
// too; this only controls visibility.
const MIGRATIONS_OWNER_EMAIL = (
  import.meta.env.VITE_MIGRATIONS_OWNER_EMAIL || 'karlreti+ml@gmail.com'
).toLowerCase();

const navItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Live Rooms', icon: <LiveTvIcon />, path: '/rooms/live' },
  { text: 'Rooms', icon: <MeetingRoomIcon />, path: '/rooms' },
  { text: 'Recordings', icon: <VideoCameraFrontIcon />, path: '/recordings' },
  { text: 'Public Files', icon: <FolderSharedIcon />, path: '/public-files' },
  { text: 'API Keys', icon: <VpnKeyIcon />, path: '/api-keys' },
  { text: 'Usage', icon: <BarChartIcon />, path: '/usage' },
  { text: 'Team', icon: <GroupIcon />, path: '/team' },
  { text: 'Billing', icon: <PaymentIcon />, path: '/billing' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { text: 'SDK Docs', icon: <MenuBookIcon />, path: 'https://sdk.hiyve.dev', external: true },
  { text: 'Migrations', icon: <SwapHorizIcon />, path: '/migrations', migrationsOwnerOnly: true },
  { text: 'User Inventory', icon: <PersonRemoveIcon />, path: '/user-removal', migrationsOwnerOnly: true },
  { text: 'Users', icon: <ManageAccountsIcon />, path: '/users', userManagerOnly: true },
  // Admin section
  { text: 'Admin', icon: <AdminPanelSettingsIcon />, path: '/admin', adminOnly: true },
];

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useUser();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin-only route wrapper
function AdminRoute({ children }) {
  const { user, isAuthenticated, isLoading } = useAppUser();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.metadata?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Login button component
function LoginButton() {
  const { user, isAuthenticated } = useUser();
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {user?.name || user?.email}
        </Typography>
        <Button color="inherit" onClick={() => logout()}>
          Logout
        </Button>
      </Box>
    );
  }

  return (
    <Button color="inherit" onClick={() => navigate('/login')}>
      Login
    </Button>
  );
}

// Wrapper to pass URL token to PasswordResetForm (supports both ?token= and /:token)
function ResetPasswordRoute() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token');
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <PasswordResetForm
        token={token}

        onSuccess={() => navigate('/login')}
        onBack={() => navigate('/login')}
      />
    </Box>
  );
}

// Wrapper to pass URL query token to EmailVerification
function VerifyEmailRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <EmailVerification
        token={token}

      />
    </Box>
  );
}

// Login page with TFA support
function LoginPage() {
  const [showTfa, setShowTfa] = useState(false);
  const navigate = useNavigate();

  if (showTfa) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
        <TfaVerification
          onSuccess={() => navigate('/dashboard')}
          onBack={() => setShowTfa(false)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <LoginForm
        onSuccess={() => navigate('/dashboard')}
        onTfaRequired={() => setShowTfa(true)}
        onRegister={() => navigate('/signup')}
        onForgotPassword={() => navigate('/forgot-password')}
      />
    </Box>
  );
}

// Main App component
function App() {
  const { user, isLoading, isAuthenticated } = useUser();
  const { user: appUser } = useAppUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const toggleDrawer = (open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Loading state
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Sidebar drawer content
  const drawerContent = (
    <Box sx={{ width: drawerWidth }} role="presentation">
      <Toolbar />
      <List>
        {navItems.filter((item) =>
          (!item.adminOnly || appUser?.metadata?.isAdmin) &&
          (!item.migrationsOwnerOnly ||
            String(appUser?.email || '').toLowerCase() === MIGRATIONS_OWNER_EMAIL) &&
          (!item.userManagerOnly || appUser?.metadata?.isUserManager),
        ).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={item.external ? 'a' : Link}
              {...(item.external
                ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                : { to: item.path }
              )}
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />

        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Toolbar>
            {isAuthenticated && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexGrow: 1 }}
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            >
              <img
                src="/rtc.png"
                alt="Logo"
                style={{ marginRight: 8, height: isMobile ? 32 : 40 }}
              />
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="h1" noWrap>
                Hiyve Admin Console
              </Typography>
            </Box>
            {isAuthenticated && !isMobile && appUser?.metadata?.organization && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {appUser.metadata.organization}
              </Typography>
            )}
            <LoginButton />
          </Toolbar>
        </AppBar>

        {/* Sidebar Drawer */}
        {isAuthenticated && (
          <Drawer
            variant="temporary"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            ModalProps={{ keepMounted: true }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: 8,
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : (
                <LoginPage />
              )
            } />
            <Route path="/signup" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : (
                <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                  <RegisterForm
            
                    onSuccess={() => navigate('/login')}
                    onLogin={() => navigate('/login')}
                  />
                </Box>
              )
            } />
            <Route path="/forgot-password" element={
              <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                <PasswordResetRequest
          
                  onSuccess={() => navigate('/login')}
                  onBack={() => navigate('/login')}
                />
              </Box>
            } />
            <Route path="/reset-password/:token" element={
              <ResetPasswordRoute />
            } />
            <Route path="/reset-password" element={
              <ResetPasswordRoute />
            } />
            <Route path="/verify-email" element={
              <VerifyEmailRoute />
            } />
            <Route path="/verification-pending" element={
              isAuthenticated && user?.emailVerified === true ? <Navigate to="/dashboard" replace /> : (
                <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                  <VerificationPending />
                </Box>
              )
            } />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/organization/setup" element={
              <ProtectedRoute><OrganizationSetup /></ProtectedRoute>
            } />
            <Route path="/rooms" element={
              <ProtectedRoute><Rooms /></ProtectedRoute>
            } />
            <Route path="/rooms/live" element={
              <ProtectedRoute><LiveRooms /></ProtectedRoute>
            } />
            <Route path="/rooms/:roomId" element={
              <ProtectedRoute><RoomDetails /></ProtectedRoute>
            } />
            <Route path="/recordings" element={
              <ProtectedRoute><Recordings /></ProtectedRoute>
            } />
            <Route path="/recordings/:recordingId" element={
              <ProtectedRoute><RecordingDetails /></ProtectedRoute>
            } />
            <Route path="/api-keys" element={
              <ProtectedRoute><ApiKeyManagement /></ProtectedRoute>
            } />
            <Route path="/usage" element={
              <ProtectedRoute><UsageDashboard /></ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute><TeamManagement /></ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute><BillingSettings /></ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute><AccountSettings /></ProtectedRoute>
            } />

            <Route path="/migrations" element={
              <ProtectedRoute><MigrationsSupport /></ProtectedRoute>
            } />
            <Route path="/user-removal" element={
              <ProtectedRoute><UserRemoval /></ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute><UserManagement /></ProtectedRoute>
            } />
            <Route path="/users/:userId" element={
              <ProtectedRoute><UserDetail /></ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <AdminRoute><AdminDashboard /></AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute><AdminUsers /></AdminRoute>
            } />
            <Route path="/admin/users/:userId" element={
              <AdminRoute><AdminUserDetails /></AdminRoute>
            } />
            <Route path="/admin/notifications" element={
              <AdminRoute><AdminNotifications /></AdminRoute>
            } />
            <Route path="/admin/infrastructure" element={
              <AdminRoute><AdminInfrastructure /></AdminRoute>
            } />
            <Route path="/admin/server-status" element={
              <AdminRoute><AdminServerStatus /></AdminRoute>
            } />
            {/* Public Files is a tenant-owner-level feature (the backend
                `/api/public-files/*` routes are gated by `tenant-owner` via
                requireUserMatch, not by `admin`). The component still lives
                under features/admin/ for historical reasons; only the route
                + sidebar position have moved out of the admin section. */}
            <Route path="/public-files" element={
              <ProtectedRoute><AdminFileManager /></ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
